import { useState } from "react";
import { Box, Button, CircularProgress, Paper, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import type { PlaybookChange } from "../../services/lensService";
import { formatWhen, plural } from "./lensUtils";

const ACTIONS: Record<string, string> = {
    "create-type": "Playbook created",
    "update-type": "Playbook updated",
    "add-provision": "Provision added",
    "edit-provision": "Provision edited",
    "approve-provision": "Provision approved",
    "return-provision-to-draft": "Provision returned to draft",
    "delete-provision": "Provision archived",
    "restore-provision": "Provision restored",
    "update-review-note": "Review note updated",
    "approve-all": "All drafts approved",
    "ai-draft-from-precedent": "Drafted from precedent by AI",
    "create-group": "Group created",
    "rename-group": "Group renamed",
    "delete-group": "Group deleted",
    "move-provisions": "Provisions moved",
    "reorder-groups": "Groups reordered",
    "reorder-provisions": "Provisions reordered",
};

type Detail = Record<string, unknown>;

const parseDetail = (detail: string | null): Detail | null => {
    if (!detail) return null;
    try {
        const parsed = JSON.parse(detail);
        return parsed && typeof parsed === "object" ? (parsed as Detail) : null;
    } catch {
        return null;
    }
};

const names = (value: unknown) => (Array.isArray(value) ? value.map(String).join(", ") : String(value ?? ""));
const size = (value: unknown) => (Array.isArray(value) ? value.length : 0);

// Group and ordering rows read as a sentence, with any list on hover.
const SUMMARIES: Record<string, (d: Detail) => { text: string; list?: unknown }> = {
    "create-group": (d) => ({ text: `Created the group "${names(d.group)}"` }),
    "rename-group": (d) => ({ text: `Renamed the group "${names(d.from)}" to "${names(d.to)}"` }),
    "delete-group": (d) => ({
        text: `Deleted the group "${names(d.group)}" · ${plural(size(d.ungrouped), "provision")} moved to Ungrouped`,
        list: d.ungrouped,
    }),
    "move-provisions": (d) => ({ text: `Moved ${plural(size(d.provisions), "provision")} to ${names(d.to)}`, list: d.provisions }),
    "reorder-groups": (d) => ({ text: "Reordered the groups", list: d.groups }),
    "reorder-provisions": (d) => ({ text: `Reordered ${names(d.groups)}` }),
};

const Summary = ({ change }: { change: PlaybookChange }) => {
    const detail = parseDetail(change.detail);
    const summary = detail && SUMMARIES[change.action]?.(detail);
    if (!summary) return null;

    const list = size(summary.list) > 0 ? names(summary.list) : null;
    const text = (
        <Typography variant="caption" component="div" style={{ color: "#555" }}>
            {list ? <span style={{ textDecoration: "underline dotted" }}>{summary.text}</span> : summary.text}
        </Typography>
    );
    return list ? <Tooltip title={list}>{text}</Tooltip> : text;
};

const COLLAPSED_COUNT = 10;

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(4), marginTop: theme.spacing(4) },
    entry: { padding: theme.spacing(2, 0), borderBottom: "1px solid #eee", "&:last-child": { borderBottom: 0 } },
    head: { display: "flex", alignItems: "baseline", gap: theme.spacing(2), flexWrap: "wrap" },
    who: { marginLeft: "auto", color: "#555" },
    muted: { color: "#777" },
}));

const describe = (value: unknown): string =>
    value !== null && typeof value === "object" ? Object.values(value).map(String).join(" / ") : String(value);

// Lists become a count with the values on hover, so an approve-all of sixty keys stays one line.
const DetailLine = ({ detail }: { detail: unknown }) => {
    let parsed = detail;
    if (typeof detail === "string") {
        try {
            parsed = JSON.parse(detail);
        } catch {
            return <Typography variant="caption">{detail}</Typography>;
        }
    }
    if (!parsed || typeof parsed !== "object") return null;

    const parts = Object.entries(parsed as Record<string, unknown>)
        .filter(([, value]) => value !== null && value !== "" && !(Array.isArray(value) && value.length === 0))
        .map(([field, value]) =>
            Array.isArray(value) ? (
                <Tooltip key={field} title={value.map(describe).join(", ")}>
                    <span style={{ textDecoration: "underline dotted" }}>
                        {value.length} {field}
                    </span>
                </Tooltip>
            ) : (
                <span key={field}>
                    {field}: {describe(value)}
                </span>
            ),
        );
    if (parts.length === 0) return null;

    return (
        <Typography variant="caption" component="div" style={{ color: "#555" }}>
            {parts.map((part, i) => (
                <span key={i}>
                    {i > 0 && " · "}
                    {part}
                </span>
            ))}
        </Typography>
    );
};

const Changelog = ({ changes, isLoading }: { changes: PlaybookChange[]; isLoading: boolean }) => {
    const { classes } = useStyles();
    const [showAll, setShowAll] = useState(false);
    const shown = showAll ? changes : changes.slice(0, COLLAPSED_COUNT);

    return (
        <Paper className={classes.root}>
            <Typography variant="h6" gutterBottom>
                Recent changes
            </Typography>
            {isLoading ? (
                <CircularProgress size={24} />
            ) : changes.length === 0 ? (
                <Typography variant="body2" className={classes.muted}>
                    No changes recorded yet.
                </Typography>
            ) : (
                <>
                    {shown.map((change, i) => (
                        <Box key={`${change.changedOn}-${i}`} className={classes.entry}>
                            <Box className={classes.head}>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    {ACTIONS[change.action] ?? change.action}
                                </Typography>
                                {change.provisionKey && (
                                    <Typography variant="caption" className={classes.muted}>
                                        {change.provisionKey}
                                    </Typography>
                                )}
                                <Typography variant="caption" className={classes.who}>
                                    {change.byName || change.byEmail || "Not recorded"}
                                </Typography>
                                <Typography variant="caption" className={classes.muted}>
                                    {formatWhen(change.changedOn)}
                                </Typography>
                            </Box>
                            {SUMMARIES[change.action] ? (
                                <Summary change={change} />
                            ) : (
                                change.detail && <DetailLine detail={change.detail} />
                            )}
                        </Box>
                    ))}
                    {changes.length > COLLAPSED_COUNT && (
                        <Button size="small" onClick={() => setShowAll((s) => !s)}>
                            {showAll ? "Show fewer" : `Show all ${changes.length}`}
                        </Button>
                    )}
                </>
            )}
        </Paper>
    );
};

export default Changelog;
